<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
            <PageHeader
                btnTitle="Add New"
                :showBtn="false"
                :showAddNew="true"
                title="Sales Invoice list"
                @onClickAddNewButton="navigateToAddPage"
            >
                <template #extra-btn>
                    <button type="button" class="btn page-head-btn page-head-btn-ghost" @click="onClickToExport">
                        <i class="fa-regular fa-file-excel me-1"></i> Export
                    </button>
                </template>
            </PageHeader>

        <div class="px-2">
            <DateQuerySetter
                class="filter-row"
                btnTitle="Go"
                @onClickGo="onClickGo"
            >
                <div class="col-auto d-flex align-items-end mt-2">
                    <FilterPopover
                        ref="filterPopoverRef"
                        title="Filters"
                        :active-count="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div class="col-12">
                            <div class="d-flex align-items-center justify-content-between filter-label">
                                <label for="colFormLabel" class="col-form-label">Select Customer </label>
                                <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                                    <i class="fas fa-search"></i>
                                </span>
                            </div>

                            <AsyncSelect
                                placeholder="Select Customer"
                                v-model="draftFilters.contactProfileId"
                                :api-service="fetchContactProfiles"
                                :additional-query="additionalContactOption"
                                :format-label="formatPatientLabel"
                                :key="contactRenderKey"
                                :reduce="item => item.id"
                            />
                        </div>
                        <div class="col-12">
                            <label for="colFormLabel" class="form-label filter-label">PO Refenrence</label>
                            <vField
                                name="reference"
                                type="text"
                                class="form-control"
                                placeholder="PO Reference"
                                v-model="draftFilters.orderNumber"
                            />
                        </div>
                        <div class="col-12">
                            <label for="colFormLabel" class="form-label filter-label">Invoice Type</label>
                            <v-select
                                placeholder="Select Bill Type"
                                v-model="draftFilters.billType"
                                :options="billTypeList"
                                label="label"
                                :reduce="label => label.value"
                            />
                        </div>
                        <div class="col-12">
                            <label for="colFormLabel" class="form-label filter-label">Template Name</label>
                            <AsyncSelect
                                placeholder="Select Template"
                                v-model="draftFilters.template"
                                class="w-100"
                                :additional-query="{ template_type: 'sale'}"
                                :api-service="fetchTemplates"
                            />
                        </div>
                        <div class="col-12">
                            <label for="colFormLabel" class="form-label filter-label">Payment Status</label>
                            <v-select
                                placeholder="Select Payment Status"
                                v-model="draftFilters.selectedPaymentStatus"
                                :options="paymentStatus"
                                label="name"
                                :reduce="option => option.value"
                            />
                        </div>
                    </FilterPopover>
                </div>
            </DateQuerySetter>
        </div>
        <div class="col-12 px-2 mt-2">
            <ListTable :purchase="purchaseList" route_name="sales-invoice-edit"/>
        </div>
        <div class="px-2"><Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/></div>
    </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import { ref, inject, onMounted, computed, watch  } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import PageHeader from '@/components/atom/PageHeader.vue'
import Pagination from '@/components/atom/Pagination'
import ListTable from '@/components/molecule/company/inventory/sales-invoice-template/ListTable.vue'
import handleContact from "@/services/modules/contact";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleTemplate from '@/services/modules/all-modules/template';
import handleInventory from "@/services/modules/inventory";

const router = useRouter()
const store = useStore()
let offset = ref(20)
let purchaseList = ref([])
const route = useRoute()
const contactRenderKey = ref(0);
const invoiceType = ref('all');
const templateName = ref('');
const paymentStatus = ref([
    {name: 'Due', value: 'due'},
    {name: 'Paid', value: 'paid'}
]);
const billTypeList = ref([]);

const getEmptyFilters = () => ({
    contactProfileId: null,
    orderNumber: '',
    billType: null,
    template: '',
    selectedPaymentStatus: null
});
const appliedFilters = ref(getEmptyFilters());
const draftFilters = ref(getEmptyFilters());
const filterPopoverRef = ref(null);
const activeFilterCount = computed(() => {
    return Object.values(appliedFilters.value).filter(v => !!v).length
});

const {fetchSalesList} = handlePurchase()
const {fetchContactProfiles} = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchTemplates } = handleTemplate();
const { fetchHome } = handleInventory();

const additionalContactOption = ref({
    with_mobile: 1,
    dealer_id: '',
    roles: 'customer'
});

const invoiceTypes = ref([
    { name: 'All', value: 'all' },
    { name: 'Template', value: 'template' }
]);

const companyId = computed(() => {
    return route.params.companyId
})
const startDate = computed(() => {
    return route.query.start
})
const endDate = computed(() => {
    return route.query.end
})
const page = computed(() => {
    return route.query.page
})

const onOpenContactSearchModal = () => {
    store.state.isModalOpenThree = true;
}

function navigateToAddPage() {
    router.push({name: 'sales-invoice-template', params: route.params, query:route.query})
}

function getQuery () {
    const filters = appliedFilters.value
    let query = '?company_id=' + companyId.value
    if(filters.contactProfileId) query += '&contact_profile_id='+filters.contactProfileId
    if(startDate.value) query += '&start_date='+startDate.value
    if(endDate.value) query += '&end_date='+endDate.value
    if(page.value) query += '&page='+page.value
    if(invoiceType.value) query += '&invoice_type=' + invoiceType.value;
    if(templateName.value) query += '&template_name=' + templateName.value;
    if(filters.template) query += '&templateable_id=' + filters.template.templateable_id;
    if(filters.billType) query += '&bill_type=' + filters.billType;
    if(filters.orderNumber) query += '&order_number=' + filters.orderNumber;
    if(filters.selectedPaymentStatus) query += '&payment_status=' + filters.selectedPaymentStatus;
    query += '&offset='+offset.value
    return query
}

function onOpenFilter() {
    draftFilters.value = Object.assign({}, appliedFilters.value)
}

function onApplyFilters() {
    appliedFilters.value = Object.assign({}, draftFilters.value)
    reloadFromFirstPage()
}

function onClearFilters() {
    appliedFilters.value = getEmptyFilters()
    draftFilters.value = getEmptyFilters()
    if (filterPopoverRef.value) filterPopoverRef.value.close()
    reloadFromFirstPage()
}

async function reloadFromFirstPage() {
    store.commit('resetPagination')
    await router.push({path: route.path, query: Object.assign({}, route.query, {page: 1})})
    onClickGo()
}

function onClickToExport() {
    let token = localStorage.getItem('token')
    let query = getQuery().replace(/&page=\d+/g, '')
    let url = `${process.env.VUE_APP_BASE_URL}/export/inventory/sales/invoices${query}&_token=${token}&is_export=1`
    let a = document.createElement("a")
    a.setAttribute("href", url)
    a.click()
}

function onClickGo() {
    fetchSalesList(getQuery())
    .then(res => {
        if(res.status) {
            purchaseList.value = res.date.data
            setPagination(res.date)
            return
        }
        purchaseList.value = []
        store.commit("resetPagination")
    })
}

function setPagination(data) {
    if(!data.data.length) return
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

function onPageChange(page) {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    router.push({path: route.path, query: routeQuery})
    onClickGo()
}

onMounted(() => {
    onClickGo();
    fetchHome().then(res => {
        if (res.data) {
          for (const value of Object.keys(res.data.hospital_invoice_types)) {
            billTypeList.value.push({
              value: value,
              label: res.data.hospital_invoice_types[value]
            })
          }
        }
    })
})

</script>

<style scoped>
.mt-tpp-1 {
    margin-top: 1%;
}
.filter-label {
    padding-right: 0.5rem !important;
}
.invoice-type {
    margin-top: 12px;
}
.page-head-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    padding: 0 16px;
    white-space: nowrap;
}
.page-head-btn-ghost {
    border: 1px solid #e4e7ec;
    background: #fff;
    color: #475467;
}
.page-head-btn-ghost:hover {
    background: #f9fafb;
    color: #101828;
}
.filter-row {
    position: relative;
}
.filter-row :deep(.filter-pop-wrap) {
    position: static;
}
.filter-row :deep(.filter-pop-wrap .filter-pop) {
    left: calc(var(--bs-gutter-x) * .5);
    right: auto;
    width: calc(100% - var(--bs-gutter-x)) !important;
    max-width: 520px;
}
</style>